// components/WelcomePopup.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Grow,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  ArrowBack as ArrowBackIcon,
  Storefront as StorefrontIcon,
  QrCode as QrCodeIcon,
  Notifications as NotificationsIcon,
  ShoppingCart as ShoppingCartIcon,
  LocationOn as LocationOnIcon,
  ToggleOn as ToggleOnIcon,
  Analytics as AnalyticsIcon,
  DeliveryDining as DeliveryDiningIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Devices as DevicesIcon,
  Dashboard as DashboardIcon,
  EmojiEvents as EmojiEventsIcon,
  AttachMoney as AttachMoneyIcon,
  SupportAgent as SupportAgentIcon,
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon
} from "@mui/icons-material";
import { useLanguage } from "../context/LanguageContext";

const Transition = React.forwardRef(function Transition(props, ref) {
  return (
    <Grow
      ref={ref}
      {...props}
      timeout={500}
      style={{ transformOrigin: "center center" }}
    />
  );
});

const WelcomePopup = ({ isOpen = false, isPart = false, onClose }) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem("jomla_welcome_seen");
    if (!hasSeenWelcome) {
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    } else if(isOpen){
      setOpen(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setOpen(false);
    onClose(false);
    localStorage.setItem("jomla_welcome_seen", "true");
  };

 const features = [
  { 
    icon: <QrCodeIcon />, 
    titleAr: "قائمة المتجر ورمز QR", 
    titleEn: "Store Menu & QR Code",
    descAr: "احصل على قائمة متجر الخاصة ورمز QR خاص بعلامتك التجارية مع شعار وصورة غلاف متجرك",
    descEn: "Get your own store menu and QR code with your brand logo and cover image"
  },
  { 
    icon: <NotificationsIcon />, 
    titleAr: "تنبيهات فورية", 
    titleEn: "Instant Notifications",
    descAr: "احصل على إشعارات الطلبات الفورية على الموقع والتطبيق للوحدة التحكم والعميل",
    descEn: "Get instant order notifications on web and app for admin and customers"
  },
  { 
    icon: <ShoppingCartIcon />, 
    titleAr: "إدارة المتجر", 
    titleEn: "Store Management",
    descAr: "أضف، حرر، وقم بإدارة أسعار المنتجات، الصور، الخصومات، والإضافات بسهولة",
    descEn: "Add, edit, and manage product prices, images, discounts, and extras easily"
  },
  { 
    icon: <LocationOnIcon />, 
    titleAr: "تحديد موقع العميل", 
    titleEn: "Customer Location",
    descAr: "تتبع مواقع العملاء مباشرة عبر خرائط جوجل",
    descEn: "Track customer locations directly via Google Maps"
  },
  { 
    icon: <ToggleOnIcon />, 
    titleAr: "التحكم بحالة المتجر", 
    titleEn: "Store Status Control",
    descAr: "يمكنك تفعيل أو تعطيل متجرك في أي وقت",
    descEn: "You can activate or deactivate your store at any time"
  },
  { 
    icon: <AnalyticsIcon />, 
    titleAr: "تحليل المبيعات", 
    titleEn: "Sales Analytics",
    descAr: "تحليل الإيرادات ونمو المبيعات من خلال لوحة تحكم سهلة",
    descEn: "Analyze revenue and sales growth through an easy dashboard"
  },
  { 
    icon: <DeliveryDiningIcon />, 
    titleAr: "إدارة التوصيل", 
    titleEn: "Delivery Management",
    descAr: "تحكم في مدى التوصيل الخاص بمتجرك بسهولة",
    descEn: "Easily control your store's delivery range"
  },
  { 
    icon: <SearchIcon />, 
    titleAr: "اكتشاف المتاجر الذكي", 
    titleEn: "Smart Store Discovery",
    descAr: "نظام يحدد تلقائيًا المتاجر القريبة بناءً على نطاق التوصيل",
    descEn: "System automatically identifies nearby stores based on delivery range"
  },
  { 
    icon: <PeopleIcon />, 
    titleAr: "أنواع الحسابات", 
    titleEn: "Account Types",
    descAr: "لدينا 3 أنواع من الحسابات: العملاء وأصحاب المتاجر والدليفري",
    descEn: "We have 3 types of accounts: Customers, Store Owners, and Delivery Drivers"
  },
  { 
    icon: <DevicesIcon />, 
    titleAr: "دعم متعدد المنصات", 
    titleEn: "Multi-Platform Support",
    descAr: "تطبيق متاح على الويب والأندرويد",
    descEn: "App available on web and Android"
  },
  { 
    icon: <DashboardIcon />, 
    titleAr: "لوحة تحكم المتجر", 
    titleEn: "Store Dashboard",
    descAr: "توفر لوحة تحكم سهلة للمتاجر على الويب والأندرويد",
    descEn: "Provides an easy dashboard for stores on web and Android"
  },
  { 
    icon: <EmojiEventsIcon />, 
    titleAr: "تجربة مجانية", 
    titleEn: "Free Trial",
    descAr: "استمتع بـ 90 يوماً من الوصول المجاني لجميع الميزات",
    descEn: "Enjoy 90 days of free access to all features"
  },
  { 
    icon: <AttachMoneyIcon />, 
    titleAr: "أسعار الاشتراك", 
    titleEn: "Subscription Pricing",
    descAr: "تتمتع بعروضنا المخفضة بدءاً من $100 لمدة 5 سنوات",
    descEn: "Enjoy our discounted offers starting from $100 for 5 years"
  },
  { 
    icon: <SupportAgentIcon />, 
    titleAr: "دعم فني على مدار 24 ساعة", 
    titleEn: "24/7 Technical Support",
    descAr: "نقدم دعمًا مجانيًا للوحدة تحكم متجرك ومساعدة العملاء في الطلبات",
    descEn: "We provide free support for your store dashboard and customer order assistance"
  }
];

const terms = [
  {
    ar: "المنصة تدعم فقط التعامل الفوري ودفع قيمة المنتج كاش عند التسليم أو عبر الفيزا",
    en: "The platform only supports immediate cash payment upon delivery or visa"
  },
  {
    ar: "لا نقوم بجمع أي معلومات شخصية تخص صاحب المتجر أو العميل",
    en: "We do not collect any personal information from store owners or customers"
  },
  {
    ar: "لا نقدم خدمة التوصيل من طرف المنصة (جميع خدمات التوصيل عليك)",
    en: "We do not provide delivery services (all delivery services are your responsibility)"
  },
  {
    ar: "المنصة توفر فرصة عرض المنتجات لعملائك عن طريق توفير داشبورد و QR Code",
    en: "The platform provides the opportunity to showcase products to your customers through a dashboard and QR Code"
  }
];

  const testCredentials = {
    email: "test_rest@gmail.com",
    password: "Test@123456"
  };

  return (
    <Dialog
      open={open}
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "32px",
          background: "linear-gradient(135deg, #0A0A0A 0%, #121212 100%)",
          overflow: "hidden",
          position: "relative",
          maxHeight: "90vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#1A1A1A",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#FFD700",
            borderRadius: "4px",
          }
        }
      }}
    >
      {/* Header Gradient */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "200px",
          background: "radial-gradient(circle at 30% 0%, rgba(255, 215, 0, 0.1), transparent)",
          pointerEvents: "none"
        }}
      />

      <DialogContent sx={{ p: { xs: 3, sm: 4, md: 5 }, position: "relative" }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            backgroundColor: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(10px)",
            zIndex: 1,
            "&:hover": {
              backgroundColor: "rgba(255,215,0,0.2)"
            }
          }}
        >
          <CloseIcon sx={{ color: "#FFD700" }} />
        </IconButton>

        {/* Logo Section - Centered */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              height: "100px",
              width: "140px",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
                filter: "drop-shadow(0 0 20px rgba(255, 215, 0, 0.5))"
              }
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 120 120"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="mainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFD700" />
                  <stop offset="50%" stopColor="#FFA500" />
                  <stop offset="100%" stopColor="#FF6347" />
                </linearGradient>
                <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#FFD700" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#FF4500" stopOpacity="0" />
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              <circle cx="60" cy="60" r="56" fill="none" stroke="url(#mainGradient)" strokeWidth="3" filter="url(#glow)" />
              <circle cx="60" cy="60" r="53" fill="none" stroke="rgba(255, 215, 0, 0.3)" strokeWidth="2" />
              <circle cx="60" cy="60" r="47" fill="url(#centerGlow)" />
              <circle cx="60" cy="60" r="44" fill="#0F0F0F" />
              
              {/* Coin Icon */}
              <g transform="translate(52, 42)">
                <circle cx="8" cy="1" r="12" fill="none" stroke="#FFD700" strokeWidth="1.5" />
                <text x="8" y="5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#FFD700" fontFamily="monospace">$</text>
              </g>
              
              <text x="60" y="72" textAnchor="middle" fontSize="18" fontWeight="800" fill="#FFD700" fontFamily="'Montserrat', 'Poppins', sans-serif" letterSpacing="1.5" stroke="#FF4500" strokeWidth="0.5" paintOrder="stroke">JOMLA</text>
              <text x="60" y="92" textAnchor="middle" fontSize="15" fontWeight="600" fill="#FFA500" fontFamily="'Montserrat', sans-serif" letterSpacing="1.8" stroke="#D32F2F" strokeWidth="0.3" paintOrder="stroke">LINE</text>
              <line x1="35" y1="78" x2="45" y2="78" stroke="#FFD700" strokeWidth="1" strokeOpacity="0.5" />
              <line x1="75" y1="78" x2="85" y2="78" stroke="#FFD700" strokeWidth="1" strokeOpacity="0.5" />
            </svg>
          </Box>
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: { xs: "24px", sm: "32px" },
            fontWeight: 800,
            textAlign: "center",
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1
          }}
        >
          Jomla Line
        </Typography>
        
        <Typography
          sx={{
            fontSize: "16px",
            color: "#B3B3B3",
            textAlign: "center",
            mb: 4
          }}
        >
          {language === 'ar' 
            ? "منصة متكاملة لإدارة متجرك الإلكتروني"
            : "Complete platform for managing your online store"}
        </Typography>

{/* Features Grid */}
<Box sx={{ mb: 4 }}>
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
    <StarIcon sx={{ color: "#FFD700" }} />
    <Typography variant="h6" sx={{ color: "#FFD700", fontWeight: 700 }}>
      {language === 'ar' ? "ميزات التطبيق" : "App Features"}
    </Typography>
  </Box>
  
  <Grid container spacing={2}>
    {features.map((feature, index) => (
      <Grid item xs={12} sm={6} key={index}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            p: 1.5,
            borderRadius: "16px",
            backgroundColor: "rgba(255,215,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(255,215,0,0.1)",
              transform: "translateX(5px)"
            }
          }}
        >
          <Box sx={{ color: "#FFD700", mt: 0.5 }}>{feature.icon}</Box>
          <Box>
            <Typography sx={{ color: "#FFF", fontWeight: 600, fontSize: "14px", mb: 0.5 }}>
              {language === 'ar' ? feature.titleAr : feature.titleEn}
            </Typography>
            <Typography sx={{ color: "#B3B3B3", fontSize: "12px" }}>
              {language === 'ar' ? feature.descAr : feature.descEn}
            </Typography>
          </Box>
        </Box>
      </Grid>
    ))}
  </Grid>
</Box>

{/* Terms Section */}
<Box sx={{ mb: 4 }}>
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
    <GavelIcon sx={{ color: "#FFD700" }} />
    <Typography variant="h6" sx={{ color: "#FFD700", fontWeight: 700 }}>
      {language === 'ar' ? "شروط الخدمة" : "Terms of Service"}
    </Typography>
  </Box>
  
  <Box sx={{ backgroundColor: "rgba(0,0,0,0.3)", borderRadius: "16px", p: 2 }}>
    {terms.map((term, index) => (
      <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
        <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: "16px", mt: 0.3 }} />
        <Typography sx={{ color: "#B3B3B3", fontSize: "13px" }}>
          {language === 'ar' ? term.ar : term.en}
        </Typography>
      </Box>
    ))}
  </Box>
</Box>
        {/* Test Credentials */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <DevicesIcon sx={{ color: "#FFD700" }} />
            <Typography variant="h6" sx={{ color: "#FFD700", fontWeight: 700 }}>
              {language === 'ar' ? "النسخة التجريبية" : "Demo Version"}
            </Typography>
          </Box>
          
          <Box sx={{ backgroundColor: "rgba(255,215,0,0.1)", borderRadius: "16px", p: 2, textAlign: "center" }}>
            <Typography sx={{ color: "#FFF", mb: 1 }}>
              {language === 'ar' 
                ? "لتجربة النسخة من لوحة تحكم المتجر قم بتسجيل الدخول باستخدام المعلومات التالية:"
                : "To try the demo version, login with the following credentials:"}
            </Typography>
            <Chip 
              label={`📧 ${testCredentials.email}`}
              sx={{ 
                backgroundColor: "rgba(255,215,0,0.2)", 
                color: "#FFD700",
                mx: 1,
                mb: 1,
                fontFamily: "monospace"
              }}
            />
            <Chip 
              label={`🔑 ${testCredentials.password}`}
              sx={{ 
                backgroundColor: "rgba(255,215,0,0.2)", 
                color: "#FFD700",
                mx: 1,
                mb: 1,
                fontFamily: "monospace"
              }}
            />
          </Box>
        </Box>

        {/* CTA Buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            fullWidth
            onClick={handleClose}
            component="a"
            href="/login"
            sx={{
              background: "linear-gradient(90deg, #FFA726 0%, #F44336 100%)",
              color: "#fff",
              fontWeight: 700,
              py: 1.5,
              borderRadius: "14px",
              textTransform: "none",
              fontSize: "16px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 28px rgba(255, 167, 38, 0.4)"
              },
              transition: "all 0.3s ease"
            }}
          >
             {language === 'ar' ? 'سجل الآن وقم بترقية حسابك لمتجر' : 'Sign up & Upgrade your account to store'}
          </Button>
          
          <Button
            fullWidth
            onClick={() => {
              handleClose();
            }}
            sx={{
              background: "linear-gradient(90deg, #2E7D32 0%, #4CAF50 100%)",
              color: "#fff",
              fontWeight: 600,
              py: 1.5,
              borderRadius: "14px",
              textTransform: "none",
              fontSize: "16px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 28px rgba(76, 175, 80, 0.4)"
              },
              transition: "all 0.3s ease"
            }}
          >
            {language === 'ar' ? 'استعرض المتاجر' : 'View Store'} {language === 'ar' ? (
              <ArrowBackIcon sx={{ ml: 1 }} />
            ) : (
              <ArrowForwardIcon sx={{ ml: 1 }} />
            )}
          </Button>
        </Box>

        {/* Support Info */}
        <Typography
          sx={{
            textAlign: "center",
            color: "#666",
            fontSize: "12px",
            mt: 3,
            pt: 2,
            borderTop: "1px solid rgba(255,215,0,0.1)"
          }}
        >
          💬 {language === 'ar' ? 'دعم فني على مدار 24 ساعة' : '24/7 Technical Support'} - 
          <span style={{ color: "#FFD700" }}> support@jomlaline.com</span>
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomePopup;